import React from "react";
import { useStudioPageChromeOverride } from "../../../common/StudioPageChromeContext";
import { PRODUCT_STEPS, ProductStepId, ReviewItem, stepIndex } from "./productSteps";
import "./product-steps.scss";

const Chevron = ({ dir = "left" }: { dir?: "left" | "right" }) => (
  <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
    <path
      d={dir === "left" ? "M12.5 4.5 7 10l5.5 5.5" : "M7.5 4.5 13 10l-5.5 5.5"}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Tick = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" aria-hidden="true">
    <path d="m4.5 10.5 3.5 3.5 7.5-8" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Dash = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
    <path d="M6 12h12" />
  </svg>
);
const Warn = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3 2 20h20zM12 10v4M12 17v.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type HeaderProps = {
  title: string;
  subtitle?: string;
  step: ProductStepId;
  onStep: (step: ProductStepId) => void;
  /** Small chip on the right, e.g. "Draft saved" or "Live · edits". */
  chip?: React.ReactNode;
  /** Steps that can't be opened yet (Add product before the draft exists). */
  lockedFrom?: ProductStepId;
};

/**
 * Puts the title in the studio's own top bar (like Karkhana), then shows a status line and the
 * 5-segment step bar. Tapping a segment jumps there.
 */
export const ProductStepHeader = ({ title, subtitle, step, onStep, chip, lockedFrom }: HeaderProps) => {
  const current = stepIndex(step);
  const lockedAt = lockedFrom ? stepIndex(lockedFrom) : PRODUCT_STEPS.length;
  // The studio top bar already has the right back button for Add / Edit product.
  useStudioPageChromeOverride({ title });
  return (
    <div className="ps-head">
      <div className="ps-head__row">
        <span className="ps-head__status">
          {`Step ${current + 1} of ${PRODUCT_STEPS.length}`}
          {subtitle ? ` · ${subtitle}` : ""}
        </span>
        {chip}
      </div>
      <nav className="ps-bar" aria-label="Steps">
        {PRODUCT_STEPS.map((s, i) => {
          const state = i < current ? "done" : i === current ? "on" : "";
          return (
            <button
              key={s.id}
              type="button"
              className={`ps-bar__seg ${state}`}
              aria-current={i === current ? "step" : undefined}
              onClick={() => onStep(s.id)}
              title={i >= lockedAt ? "Opens after the draft is saved" : s.hint}
            >
              <span className="ps-bar__num">{i < current ? <Tick /> : i + 1}</span>
              <span className="ps-bar__label">{s.label}</span>
              <small className="ps-bar__hint">{s.hint}</small>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

/** A step's content. Stays mounted when hidden so refs, uploads and validation keep working. */
export const ProductStepPanel = ({
  id,
  active,
  children,
}: {
  id: ProductStepId;
  active: ProductStepId;
  children: React.ReactNode;
}) => (
  <section className="ps-panel" data-product-step={id} hidden={id !== active} aria-hidden={id !== active}>
    {children}
  </section>
);

/** Sticky bar at the bottom: Back on the left, the step's main action on the right. */
export const ProductStepFooter = ({ children }: { children: React.ReactNode }) => (
  <div className="ps-foot">{children}</div>
);

export const StepButton = ({
  kind = "line",
  wide,
  next,
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { kind?: "blue" | "line" | "ghost"; wide?: boolean; next?: boolean }) => (
  <button type="button" className={`ps-btn ps-btn--${kind}${wide ? " ps-btn--wide" : ""}`} {...rest}>
    <span>{children}</span>
    {next ? <Chevron dir="right" /> : null}
  </button>
);

/** Collapsible group for the Details step. */
export const DetailsGroup = ({
  title,
  summary,
  defaultOpen,
  badge,
  children,
}: {
  title: string;
  summary?: string;
  defaultOpen?: boolean;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <details className="ps-group" open={defaultOpen}>
    <summary>
      <span className="ps-group__title">
        {title}
        {summary ? <small>{summary}</small> : null}
      </span>
      {badge}
      <span className="ps-group__chev" aria-hidden="true">
        <Chevron dir="right" />
      </span>
    </summary>
    <div className="ps-group__body">{children}</div>
  </details>
);

/** Review step list: each open item jumps to the step where it's filled. */
export const ReviewChecklist = ({
  items,
  onJump,
}: {
  items: ReviewItem[];
  onJump: (step: ProductStepId, focusId?: string) => void;
}) => (
  <ul className="ps-review">
    {items.map((item) => (
      <li key={item.id} className={item.soft ? "soft" : item.ok ? "ok" : "todo"}>
        <span className="ps-review__icon">{item.soft ? <Dash /> : item.ok ? <Tick /> : <Warn />}</span>
        <span className="ps-review__label">{item.label}</span>
        <span className="ps-review__detail">{item.detail}</span>
        {item.ok && !item.soft ? null : (
          <button type="button" className="ps-btn ps-btn--line ps-btn--sm" onClick={() => onJump(item.step, item.focusId)}>
            <span>{item.soft ? "Add" : "Fill"}</span>
          </button>
        )}
      </li>
    ))}
  </ul>
);
